import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "@hooks/useNotification";
import "date-fns";
import moment from "moment-timezone";
import { useForm, Controller } from "react-hook-form";
import "@responsivity/responsivityMobile/Employer/parcoursCertifications.css";
import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import { Switch, Input, TextArea } from "@components/tailwindComponents";
import { checkErrors } from "@helpers/_functions";
import { EmployerCourse } from "@services/all.services";
import { NotificationUtil } from "@utils/notification.utils";

const PERIODIC_EVENT = "period";
const PUNCTUAL_EVENT = "event";
const DATE_FORMAT = "YYYY-MM-DD";

export default function ModalNewCourseForm({
  isOpen,
  onClickClose,
  career = null,
  employeeId,
  hiringDate,
}) {
  const { t } = useTranslation("pages.employés.parcours&Certifications");
  const { control, setValue, handleSubmit, reset, watch } = useForm();
  const { showError, showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(PERIODIC_EVENT);
  const [inProgress, setInProgress] = useState(false);
  const [startDate, endDate] = watch(["startDate", "endDate"]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedType(PERIODIC_EVENT);
      setInProgress(false)
      reset();
    } else if (career?._id) {
      reset({
        startDate: moment(career?.dateStart, DATE_FORMAT).toDate(),
        endDate: career?.endDate ? moment(career?.endDate, DATE_FORMAT).toDate() : null,
        title: career?.title,
        note: career?.note,
        description: career?.description || "",
      });
      setInProgress(career?.progress);
      if (career?.status) setSelectedType(PUNCTUAL_EVENT);
    }
  }, [isOpen, career]);

  useEffect(() => {
    if (selectedType === PUNCTUAL_EVENT || inProgress)
      setValue("endDate", null);
  }, [selectedType, inProgress]);

  const tooglePeriod = (selected) => {
    const tooglePeriodValue = selected !== selectedType ? selected : null;
    if (selected === PUNCTUAL_EVENT) setInProgress(false);
    setSelectedType(tooglePeriodValue);
  };
  const toggleInProgress = () => {
    setInProgress(!inProgress);
    setSelectedType(PERIODIC_EVENT);
  };
  const onSubmitError = (error) => {
    const errorArray = Object.values(error);
    if (checkErrors(errorArray, 'required')) {
      showError("field_obligatory");
      return;
    }

    if (checkErrors(errorArray, 'startIsBeforeEnd'))
      return showError("verification_two_object", {
        object1: "endDate",
        operator: "superiour",
        object2: "startDate",
      });
    if (checkErrors(errorArray, 'isNotBeforeHiringDate'))
      return showError("verification_two_object", {
        object1: "startDateUpper",
        operator: "superiour",
        object2: "hiringDate",
      });
  };
  const onClickSave = async (formValues) => {
    setIsLoading(true);
    let dataCourse = {};
    let id = employeeId || window.location.href.split("/")[6];
    // @ts-ignore
    dataCourse = {
      title: formValues?.title,
      status: Boolean(selectedType === PUNCTUAL_EVENT),
      dateStart: moment(formValues.startDate).format(DATE_FORMAT),
      endDate: formValues.endDate ? moment(formValues.endDate).format(DATE_FORMAT) : '',
      Employer: id,
      progress: inProgress,
      note: formValues.note,
      User: localStorage.getItem("idAdminEnterprise"),
      Enterprise: localStorage.getItem("enterpriseId"),
      description: formValues.description || "",
    };

    let response = null;
    if (career?._id) {
      dataCourse._id = career._id;
      response = await EmployerCourse.updateById(dataCourse);
    } else {
      response = await EmployerCourse.insert(dataCourse);
    }

    if (!response?.data?.success) {
      showError(response?.data?.message, { date: response?.data?.date })
      setIsLoading(false);
      throw new Error(
        `Erreur ${career?._id ? "modification" : "insertion"} course`,
      );

    }
    let message = career?._id
      ? "update_action_success"
      : "creation_action_success";
    showSuccess(message, { object: "course" });
    NotificationUtil.emploieCourseUpdate(response?.data?.data?.data);
    setIsLoading(false);
    onClickClose()
  };

  return (
    <ModalIndex
      isOpen={isOpen}
      title={t("Ajouter/éditer le parcours")}
      onClickClose={onClickClose}
      buttonOptions={{
        click: handleSubmit(onClickSave, onSubmitError),
        color: "green",
        title: t("Enregistrer"),
      }}
      buttonCancelOptions={{
        click: onClickClose,
        color: "outlined",
        title: t("Annuler"),
      }}
      isLoading={isLoading}
    >
      <div className="!arh-mt-0 arh-block arh-space-y-2 !arh-px-0 !arh-py-0">
        <Controller
          name="title"
          control={control}
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <Input
              label={t("Titre")}
              value={value}
              name={name}
              onChange={onChange}
              invalid={invalid}
              placeholder={t("Titre")}
            />
          )}
        />
        <div className="arh-flex arh-items-center arh-justify-end arh-space-x-3">
          <div className="arh-flex arh-items-center arh-justify-end">
            <div
              className="arh-font-poppins arh-text-xs arh-font-normal arh-text-black"
              onClick={() => tooglePeriod(PERIODIC_EVENT)}
            >
              {t("Période")}
            </div>
            <label
              className={
                "custom_radio_parcours " +
                (selectedType === PERIODIC_EVENT ? "active" : "")
              }
              onClick={() => tooglePeriod(PERIODIC_EVENT)}
            ></label>
          </div>
          <div className="arh-flex arh-items-center arh-justify-end">
            <div
              className="arh-font-poppins arh-text-xs arh-font-normal arh-text-black"
              onClick={() => !inProgress && tooglePeriod(PUNCTUAL_EVENT)}
            >
              {t("Evénement ponctuel")}
            </div>
            <label
              className={
                "custom_radio_parcours " +
                (selectedType === PUNCTUAL_EVENT ? "active" : "")
              }
              onClick={() => !inProgress && tooglePeriod(PUNCTUAL_EVENT)}
            ></label>
          </div>
        </div>
        <div className="filterDate  wadeDatePicker arh-relative arh-flex arh-items-center arh-space-x-3">
          <div className="arhDatePicker employerThemeDatePicker calendar_with_btn datepicker_vacation arh-relative">
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: true,
                validate: {
                  isNotBeforeHiringDate: (value) =>
                    hiringDate && value
                      ? Boolean(moment(hiringDate, 'YYYY-MM-DD').isSameOrBefore(moment(value), "day"))
                      : true,
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { invalid },
              }) => (
                <DefaultDatePicker
                  className="datepickerDropdown GreenThemeSelect"
                  placeholder={t("Date de début")}
                  dateValue={value}
                  setDateValue={onChange}
                  invalid={invalid}
                  classPosition="calendar_left"
                // maxDate={new Date()}
                />
              )}
            />
          </div>
          <div className="arhDatePicker employerThemeDatePicker calendar_with_btn datepicker_vacation arh-relative">
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: Boolean(
                  selectedType === PERIODIC_EVENT && !inProgress && !endDate,
                ),
                validate: {
                  startIsBeforeEnd: () =>
                    selectedType === PERIODIC_EVENT &&
                      !inProgress &&
                      endDate &&
                      startDate
                      ? Boolean(moment(startDate).isSameOrBefore(endDate, "day"))
                      : true,
                },
              }}
              render={({
                field: { onChange, value, name },
                fieldState: { invalid },
              }) => (
                <DefaultDatePicker
                  className="datepickerDropdown GreenThemeSelect"
                  name={name}
                  readOnly={selectedType === PUNCTUAL_EVENT || inProgress}
                  placeholder={t("Date de fin")}
                  dateValue={value}
                  setDateValue={onChange}
                  invalid={invalid}
                  classPosition="calendar_left"
                // maxDate={new Date()}
                />
              )}
            />
          </div>
        </div>
        <div className="arh-flex arh-w-full arh-items-center arh-justify-end">
          <Switch
            color="arh-bg-greenColor"
            checked={inProgress}
            handleChange={toggleInProgress}
            disabled={Boolean(selectedType === PUNCTUAL_EVENT)}
          />
          <span className="arh-ml-2 arh-font-poppins arh-text-xs arh-font-normal arh-text-black">
            {t("En cours")}
          </span>
        </div>
        <Controller
          name="note"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <TextArea
              label={t("Notes")}
              value={value}
              name={name}
              onChange={onChange}
              invalid={invalid}
              placeholder={t("Notes")}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <TextArea
              label={t("Description du poste")}
              value={value}
              name={name}
              onChange={onChange}
              invalid={invalid}
              placeholder={t("Description du poste")}
            />
          )}
        />
      </div>
    </ModalIndex>
  );
}
